import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function McqMatrixRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const rows = Array.isArray(item?.body?.rows) ? item.body.rows : [];
  const cols = Array.isArray(item?.body?.cols) ? item.body.cols : [];

  // یک نرمالایزر کوچک: هر مقدار را به آرایه‌ای از کلیدها تبدیل می‌کند
  const normalizeToArrayMap = (raw) => {
    if (!raw || typeof raw !== "object") return {};
    const out = {};
    Object.entries(raw).forEach(([rk, v]) => {
      if (Array.isArray(v)) {
        out[rk] = v.map((x) => String(x));
      } else if (v != null && v !== "") {
        out[rk] = [String(v)];
      } else {
        out[rk] = [];
      }
    });
    return out;
  };

  // مقدار اولیه
  const initialMap = useMemo(() => {
    if (!initialAnswers || typeof initialAnswers !== "object") return {};
    const base =
      initialAnswers.selections &&
      typeof initialAnswers.selections === "object"
        ? initialAnswers.selections
        : initialAnswers;

    return normalizeToArrayMap(base);
  }, [initialAnswers]);

  const [selections, setSelections] = useState(initialMap);

  useEffect(() => {
    setSelections(initialMap);
  }, [initialMap]);

  // ✅ چندجوابی: toggle یک ستون در یک ردیف
  const handleToggle = (rowKey, colKey) => {
    setSelections((prev) => {
      const prevArr = Array.isArray(prev[rowKey]) ? prev[rowKey] : [];
      const exists = prevArr.includes(colKey);
      const row = rows.find((r) => r.key === rowKey); // پیدا کردن ردیف
      const maxCorrect = row ? row.correctCount : 1; // تعداد جواب‌های صحیح مجاز برای این ردیف
      const nextArr = exists
        ? prevArr.filter((k) => k !== colKey)
        : [...prevArr, colKey];

      // اگر تعداد انتخاب‌ها از حد مجاز بیشتر شد، هیچ تغییری ایجاد نمی‌شود
      if (nextArr.length <= maxCorrect) {
        const next = {
          ...prev,
          [rowKey]: nextArr,
        };

        onAnswerChange?.(next);

        return next;
      }

      return prev; // در غیر این صورت تغییرات اعمال نمی‌شود
    });
  };

  if (!rows.length || !cols.length) {
    return (
      <div className="text-sm text-red-600">
        {t("preview.kind_not_supported", { kind: "mcq_matrix" })}
      </div>
    );
  }

  const stemHtml =
    item?.stem?.fa || item?.stem?.en || t("preview.no_stem", "بدون متن");

  return (
    <div dir={isRtl ? "rtl" : "ltr"}>
      {/* Stem */}
      <div className="prose prose-sm max-w-none mb-4">
        <div dangerouslySetInnerHTML={{ __html: stemHtml }} />
        {/* {typeof questionNumber === "number" && (
          <div className="text-xs text-gray-500 mt-1">
            {t("preview.question_number", {
              n: questionNumber,
            })}
          </div>
        )} */}
      </div>

      {/* Matrix table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-200 text-sm bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b border-gray-200 px-3 py-2 text-gray-700 text-start">
                {t("mcq_matrix.row_header", "مورد / عبارت")}
              </th>
              {cols.map((col) => {
                const label = col?.label?.fa || col?.label?.en || col.key;
                return (
                  <th
                    key={col.key}
                    className="border-b border-gray-200 px-3 py-2 text-gray-700 text-center"
                  >
                    {label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const rowLabel =
                row?.label?.fa || row?.label?.en || row.key || "";
              const selectedForRow = Array.isArray(selections[row.key])
                ? selections[row.key]
                : [];

              return (
                <tr key={row.key} className="hover:bg-gray-50">
                  <td className="border-b border-gray-200 px-3 py-2">
                    {rowLabel}
                  </td>
                  {cols.map((col) => {
                    const colLabel =
                      col?.label?.fa || col?.label?.en || col.key;
                    const checked = selectedForRow.includes(col.key);

                    return (
                      <td
                        key={col.key}
                        className="border-b border-gray-200 px-3 py-2 text-center"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggle(row.key, col.key)}
                          aria-label={`${rowLabel} → ${colLabel}`}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
